import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { saveComments } from "../../store/slices/engagementManagerSlice";
import { fetchOrderDetails } from "../../store/slices/resourceManagerSlice";
import { useI18n } from "../../hooks/useI18n";
import { ToastMessage } from "../CommonUIComponents/ToastMessage/ToastMessage";
import CommentThread from "./CommentThread";
import CommentInput from "./CommentInput";
import "./CommentsSection.scss";

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function generateId(prefix = "c") {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function countAllComments(comments) {
  let count = 0;
  for (const c of comments) {
    count += 1;
    if (c.replies?.length) count += countAllComments(c.replies);
  }
  return count;
}

function addReplyToTree(comments, parentId, newReply) {
  for (const comment of comments) {
    const id = comment.commentId || comment.replyId;
    if (id === parentId) {
      if (!comment.replies) comment.replies = [];
      comment.replies.push(newReply);
      return true;
    }
    if (comment.replies?.length) {
      if (addReplyToTree(comment.replies, parentId, newReply)) return true;
    }
  }
  return false;
}

function toggleReactionInTree(comments, targetId, reactionType, userEmail, userName) {
  for (const comment of comments) {
    const id = comment.commentId || comment.replyId;
    if (id === targetId) {
      if (!comment.reactionDetails) comment.reactionDetails = [];

      const existingIdx = comment.reactionDetails.findIndex(
        (r) => r.userMailId?.toLowerCase() === userEmail?.toLowerCase(),
      );

      if (existingIdx >= 0) {
        const existing = comment.reactionDetails[existingIdx];
        if (existing.reaction === reactionType) {
          comment.reactionDetails.splice(existingIdx, 1);
        } else {
          existing.reaction = reactionType;
        }
      } else {
        comment.reactionDetails.push({
          userName: userName || userEmail,
          userMailId: userEmail,
          reaction: reactionType,
        });
      }
      return true;
    }
    if (comment.replies?.length) {
      if (toggleReactionInTree(comment.replies, targetId, reactionType, userEmail, userName))
        return true;
    }
  }
  return false;
}

const CommentsSection = ({ comments = [], requestId, domain, readOnly = false }) => {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const [localComments, setLocalComments] = useState([]);
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const currentUser = useSelector((state) => state.user.user);
  const { saveCommentsLoading } = useSelector((state) => state.engagementManager);
  const currentUserEmail = currentUser?.username || currentUser?.email || "";
  const currentUserName =
    [currentUser?.name, currentUser?.displayName]
      .filter(Boolean)
      .join("") || currentUserEmail.split("@")[0];

  const showToast = useCallback((message, variant) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, variant });
    toastTimerRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  useEffect(() => {
    setLocalComments(Array.isArray(comments) ? deepClone(comments) : []);
  }, [comments]);

  const persistComments = useCallback(
    async (updatedComments, { silent = false } = {}) => {
      if (!requestId) return;
      const result = await dispatch(
        saveComments({
          requestId: Number(requestId),
          comments: updatedComments,
          domain,
        }),
      );
      if (!result.error) {
        if (!silent) showToast("Comment saved successfully!", "success");
        dispatch(
          fetchOrderDetails({
            requestId: Number(requestId),
            domain,
            engagementManagerMailId: currentUserEmail || null,
          }),
        );
      } else {
        const errMsg =
          typeof result.payload === "string"
            ? result.payload
            : result.payload?.message || "Failed to save comment";
        showToast(errMsg, "error");
      }
    },
    [dispatch, requestId, domain, showToast],
  );

  const handleAddComment = useCallback(
    (message) => {
      const newComment = {
        commentId: generateId("c"),
        authorName: currentUserName,
        authorMailId: currentUserEmail,
        message,
        createdAt: new Date().toISOString(),
        reactionDetails: [],
        replies: [],
      };
      const updated = [...deepClone(localComments), newComment];
      setLocalComments(updated);
      persistComments(updated);
    },
    [localComments, currentUserName, currentUserEmail, persistComments],
  );

  const handleReply = useCallback(
    (parentId, message) => {
      const newReply = {
        replyId: generateId(`${parentId}-r`),
        authorName: currentUserName,
        authorMailId: currentUserEmail,
        message,
        createdAt: new Date().toISOString(),
        reactionDetails: [],
        replies: [],
      };
      const updated = deepClone(localComments);
      addReplyToTree(updated, parentId, newReply);
      setLocalComments(updated);
      persistComments(updated);
    },
    [localComments, currentUserName, currentUserEmail, persistComments],
  );

  const handleToggleReaction = useCallback(
    (commentId, reactionType) => {
      const updated = deepClone(localComments);
      toggleReactionInTree(
        updated,
        commentId,
        reactionType,
        currentUserEmail,
        currentUserName,
      );
      setLocalComments(updated);
      persistComments(updated, { silent: true });
    },
    [localComments, currentUserEmail, currentUserName, persistComments],
  );

  const totalCount = countAllComments(localComments);

  if (readOnly && localComments.length === 0) return null;

  return (
    <div className="comments-section">
      <div className="comments-section-header">
        <h3 className="comments-title motif-h6-default-bold">
          {t("comments_title", { defaultValue: "Comments" })}
          {totalCount > 0 && (
            <span className="comments-count">{totalCount}</span>
          )}
        </h3>
        {saveCommentsLoading && (
          <span className="comments-saving motif-body2-default-regular">
            Saving...
          </span>
        )}
      </div>

      <div className="comments-list">
        {localComments.length === 0 ? (
          <p className="comments-empty motif-body1-default-regular">
            {t("comments_noComments", {
              defaultValue: "No comments yet. Be the first to comment.",
            })}
          </p>
        ) : (
          localComments.map((comment) => (
            <CommentThread
              key={comment.commentId}
              comment={comment}
              depth={0}
              currentUserEmail={currentUserEmail}
              onReply={handleReply}
              onToggleReaction={handleToggleReaction}
              readOnly={readOnly}
            />
          ))
        )}
      </div>

      {!readOnly && (
        <div className="comments-new">
          <CommentInput
            onSubmit={handleAddComment}
            placeholder={t("comments_addPlaceholder", {
              defaultValue: "Add a comment...",
            })}
            submitLabel={t("comments_submit", { defaultValue: "Post" })}
          />
        </div>
      )}

      {toast && (
        <ToastMessage
          variant={toast.variant}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default CommentsSection;
